import { useState } from "react";
import { ComboSectionType } from "../types/option";

type SectionSelections = Record<number, number>;

export const getSelectedCount = (
  sectionSelections: SectionSelections,
): number =>
  Object.values(sectionSelections).reduce((sum, qty) => sum + qty, 0);

const isSectionFull = (
  delta: number,
  selectedTotal: number,
  requiredSelections: number,
): boolean => delta > 0 && selectedTotal >= requiredSelections;

const useComboSelections = (
  sections: ComboSectionType[],
  initialSelections: Record<number, SectionSelections> = {},
) => {
  const [selections, setSelections] =
    useState<Record<number, SectionSelections>>(initialSelections);

  const isValid = sections.every(
    (section) =>
      getSelectedCount(selections[section.id] ?? {}) >=
      section.requiredSelections,
  );

  const handleRadioSelect = (section: ComboSectionType, productId: number) => {
    setSelections((prev) => ({
      ...prev,
      [section.id]: { [productId]: 1 },
    }));
  };

  const handleStepperChange = (
    section: ComboSectionType,
    productId: number,
    delta: number,
  ) => {
    setSelections((prev) => {
      const sectionSel = prev[section.id] ?? {};
      const current = sectionSel[productId] ?? 0;
      const selectedTotal = getSelectedCount(sectionSel);

      if (isSectionFull(delta, selectedTotal, section.requiredSelections))
        return prev;

      const next = Math.max(0, current + delta);
      return {
        ...prev,
        [section.id]: { ...sectionSel, [productId]: next },
      };
    });
  };

  const getSectionSelections = (sectionId: number): SectionSelections =>
    selections[sectionId] ?? {};

  return {
    isValid,
    handleRadioSelect,
    handleStepperChange,
    getSectionSelections,
  };
};

export default useComboSelections;
